import {Component, ErrorInfo, ReactNode} from 'react';
import {AlertTriangle, RefreshCw} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class InventoryErrorBoundary extends Component<Props, State> {
  constructor (props: Props) {
    super(props);
    this.state = {hasError: false};
  }

  static getDerivedStateFromError (error: Error): State {
    return {hasError: true, error};
  }

  componentDidCatch (error: Error, errorInfo: ErrorInfo) {
    this.setState({error, errorInfo});
  }

  handleRetry = () => {
    this.setState({hasError: false, error: undefined, errorInfo: undefined});
  };

  render () {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                حدث خطأ في تحميل بيانات المخزون
              </h2>
              <p className="text-gray-600 mb-4">
                عذراً، حدث خطأ غير متوقع أثناء تحميل بيانات المخزون. يرجى المحاولة مرة أخرى.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <details className="text-left bg-gray-50 p-4 rounded-lg mb-4">
                  <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                    تفاصيل الخطأ (للمطورين)
                  </summary>
                  <pre className="text-xs text-red-600 overflow-auto">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <Button
              onClick={this.handleRetry}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              إعادة المحاولة
            </Button>
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default InventoryErrorBoundary;
